import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Sparkles, X, ArrowRight, BookOpen } from "lucide-react";

/**
 * First-login welcome dialog. Auto-mounts on any in-app page and triggers
 * exactly once per user — driven by the `tutorial_seen` flag persisted on
 * the user profile.
 *
 * Two buttons:
 *   - "Show me around" → mark seen, navigate to /app/tutorial
 *   - "Skip for now"   → mark seen, dismiss in place
 */
export default function WelcomeTourModal() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only auto-show on in-app pages, not on landing/auth/onboarding/session
    if (!pathname.startsWith("/app/")) return;
    // Already on the tutorial — no need to nag
    if (pathname.startsWith("/app/tutorial")) return;
    if (!user) return;
    if (user.tutorial_seen) return;
    // Tiny delay so the user sees the dashboard render first
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [user, pathname]);

  const markSeen = async () => {
    try {
      await api.put("/auth/profile", { tutorial_seen: true });
      await refreshUser();
    } catch {
      // best-effort; the modal still dismisses
    }
  };

  const takeTour = async () => {
    setOpen(false);
    await markSeen();
    navigate("/app/tutorial");
  };

  const skip = async () => {
    setOpen(false);
    await markSeen();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="welcome-tour-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-tour-title"
      onClick={skip}
    >
      <div
        className="relative w-full max-w-lg border border-white/15 bg-[#121212] text-white p-7 sm:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={skip}
          className="absolute top-3 right-3 text-white/40 hover:text-white p-1"
          aria-label="Dismiss"
          data-testid="welcome-tour-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="inline-flex items-center gap-2 border border-[#6B9BFF]/40 bg-[#6B9BFF]/10 px-3 py-1.5 mb-5">
          <Sparkles className="w-3.5 h-3.5 text-[#6B9BFF]" />
          <span className="label-eyebrow text-[#6B9BFF]">New here?</span>
        </div>
        <h2
          id="welcome-tour-title"
          className="font-display text-4xl sm:text-5xl leading-none mb-3"
        >
          Welcome to <span className="text-[#6B9BFF]">DAIONE</span>.
        </h2>
        <p className="text-white/70 text-sm sm:text-base mb-7 max-w-md">
          We&rsquo;ve put together a quick 9-section walkthrough covering every feature —
          dashboards, AI workouts, photo meal logging, goals, Spotify and more. It takes about
          three minutes.
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3">
          <button
            type="button"
            onClick={skip}
            className="text-xs uppercase tracking-widest text-white/60 hover:text-white px-4 py-3"
            data-testid="welcome-tour-skip"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={takeTour}
            className="flex-1 sm:flex-none bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white transition-colors inline-flex items-center justify-center gap-2"
            data-testid="welcome-tour-start"
          >
            <BookOpen className="w-3.5 h-3.5" /> Show me around <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
